import {
  DataTable,
  Badge,
  Input
} from '@tonic/shared/components/ui';
import PropTypes from "prop-types";
import { useInventoryItems } from "../../hooks/useInventory";

/**
 * @typedef {Object} InventoryItem
 * @property {string} id - Item ID
 * @property {string} name - Item name
 * @property {string} sku - Item SKU
 * @property {number} quantity - Item quantity
 */

/**
 * @typedef {Object} SelectedItem
 * @property {string} id - Item ID
 * @property {number} quantity - Selected quantity
 */

/**
 * @typedef {Object} TransferableItemsTableProps
 * @property {string} locationId - ID of the source location
 * @property {SelectedItem[]} selectedItems - Currently selected items
 * @property {(items: SelectedItem[]) => void} onItemsChange - Callback when selected items change
 */

/**
 * Table component for selecting items to transfer
 * @param {TransferableItemsTableProps} props
 * @returns {JSX.Element} The transferable items table component
 */
export function TransferableItemsTable({
  locationId,
  selectedItems,
  onItemsChange,
}) {
  const { data: items, isLoading } = useInventoryItems({
    locationId,
    status: "available",
  });

  /**
   * Handles quantity change for an item
   * @param {string} itemId - ID of the item
   * @param {string} value - New quantity value
   */
  const handleQuantityChange = (itemId, value) => {
    const quantity = parseInt(value) || 0;
    const item = items?.find((i) => i.id === itemId);

    if (quantity > (item?.quantity || 0)) {
      return;
    }

    if (quantity === 0) {
      onItemsChange(selectedItems.filter((i) => i.id !== itemId));
    } else {
      const existingItem = selectedItems.find((i) => i.id === itemId);
      if (existingItem) {
        onItemsChange(
          selectedItems.map((i) =>
            i.id === itemId ? { ...i, quantity } : i
          )
        );
      } else {
        onItemsChange([...selectedItems, { id: itemId, quantity }]);
      }
    }
  };

  const columns = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.name}</div>
          <div className="text-sm text-muted-foreground">
            SKU: {row.original.sku}
          </div>
        </div>
      ),
    },
    {
      accessorKey: "quantity",
      header: "Available Quantity",
      cell: ({ row }) => (
        <Badge variant="outline" className="font-mono">
          {row.original.quantity}
        </Badge>
      ),
    },
    {
      id: "transfer",
      header: "Transfer Quantity",
      cell: ({ row }) => {
        const selectedItem = selectedItems.find((i) => i.id === row.original.id);
        return (
          <Input
            type="number"
            min="0"
            max={row.original.quantity}
            value={selectedItem?.quantity || ""}
            onChange={(e) => handleQuantityChange(row.original.id, e.target.value)}
            className="w-24"
          />
        );
      },
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={items || []}
      isLoading={isLoading}
    />
  );
}

TransferableItemsTable.propTypes = {
  locationId: PropTypes.string.isRequired,
  selectedItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      quantity: PropTypes.number.isRequired,
    })
  ).isRequired,
  onItemsChange: PropTypes.func.isRequired,
}; 
